import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { dropsService, settingsService, surfaceRatesService } from '../services/api';
import { EstimatorModal } from '../components/EstimatorModal';
import { AreaDrawBar } from '../components/AreaDrawBar';
import { loadGoogleMaps } from '../utils/googleMaps';
import { startAreaDraw } from '../utils/mapAreaDraw';
import { formatMoney } from '../utils/pricing';
import { fmtMeasure } from '../utils/measure';
import './DropDetail.css';

export const DROP_STATUS = {
  to_do: { label: 'To do', colour: '#9CA3AF', fill: 0.16 },
  dropped: { label: 'Dropped', colour: '#34D399', fill: 0.42 },
  no_junk_mail: { label: 'No junk mail', colour: '#EF4444', fill: 0.42 },
  skipped: { label: 'Skipped', colour: '#6B7280', fill: 0.3 },
  booked: { label: 'Booked', colour: '#FBBF24', fill: 0.45 }
};
const MEASURED_STROKE = '#2DD4BF';

const shortAddress = (p) => `${p.unit_number ? `U${p.unit_number}/` : ''}${p.street_number || ''} ${p.street_full || ''}`.trim() || p.address;
const fmtTime = (v) => (v ? new Date(v).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', weekday: 'short', hour: 'numeric', minute: '2-digit', hour12: true }) : '');
function parseLot(p) {
  if (!p?.lot_geojson) return null;
  try { return JSON.parse(p.lot_geojson); } catch { return null; }
}
const surfacesOf = (p) => (p.measurements || []).filter((m) => m.measure !== 'exclude');
const metresBetween = (a, b) => {
  const R = 6371000, rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};
function pointInRing([x, y], ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
/** Centroid ([lng, lat]) of the first traced area's outer ring, or null. */
function firstCentroid(measurements) {
  const m = (measurements || []).find((x) => x.kind === 'polygon' && x.measure !== 'exclude');
  let g; try { g = m && (typeof m.geojson === 'string' ? JSON.parse(m.geojson) : m.geojson); } catch { return null; }
  const ring = g?.coordinates?.[0];
  if (!Array.isArray(ring) || !ring.length) return null;
  return [ring.reduce((s, q) => s + Number(q[0]), 0) / ring.length, ring.reduce((s, q) => s + Number(q[1]), 0) / ring.length];
}
/** The letterbox whose lot boundary holds the point, or null (a driveway's centroid often sits on the verge). */
function lotHolding(c, properties) {
  for (const p of properties) {
    const lot = parseLot(p);
    const ring = lot?.type === 'Polygon' ? lot.coordinates?.[0] : null;
    if (ring && pointInRing(c, ring)) return p;
  }
  return null;
}
function nearestLetterbox(c, properties) {
  let best = null, bestD = Infinity;
  for (const p of properties) { const d = metresBetween(p, { lat: c[1], lng: c[0] }); if (d < bestD) { bestD = d; best = p; } }
  return best;
}
/** Metres between a point and the centroid of the farthest traced polygon — a trace on another lot is drawn on the wrong house. */
function farthestShapeM(measurements, p) {
  const R = 6371000, rad = (d) => (d * Math.PI) / 180;
  let worst = 0;
  for (const m of measurements || []) {
    let g; try { g = typeof m.geojson === 'string' ? JSON.parse(m.geojson) : m.geojson; } catch { continue; }
    const ring = m.kind === 'polygon' ? g?.coordinates?.[0] : g?.coordinates;
    if (!Array.isArray(ring) || !ring.length) continue;
    const lng = ring.reduce((s, q) => s + Number(q[0]), 0) / ring.length, lat = ring.reduce((s, q) => s + Number(q[1]), 0) / ring.length;
    const dLat = rad(lat - p.lat), dLng = rad(lng - p.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(p.lat)) * Math.cos(rad(lat)) * Math.sin(dLng / 2) ** 2;
    worst = Math.max(worst, 2 * R * Math.asin(Math.sqrt(s)));
  }
  return worst;
}
const lotNumber = (p) => `${p.unit_number ? `U${p.unit_number}/` : ''}${p.street_number || '·'}`;
const tracedArea = (p) => surfacesOf(p).reduce((s, m) => s + (Number(m.measured_value) || 0), 0);

/**
 * One letterbox drop: the lots on the satellite map coloured by what happened
 * at the letterbox, a panel for the one you tap, step-through tracing of the
 * driveways with the estimator, and the walk list for the day itself.
 */
export default function DropDetail() {
  const { id } = useParams();
  const [drop, setDrop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({});
  const [surfaces, setSurfaces] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState('map'); // map | walk
  const [traceIndex, setTraceIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [lastSaved, setLastSaved] = useState('');
  const [notice, setNotice] = useState('');
  const [mapReady, setMapReady] = useState(false);
  // Adding an area (backlog #694): null | { phase: 'drawing', corners } | { phase: 'checking', ring }
  // | { phase: 'preview' | 'adding', ring, count_new, count_existing, properties }
  const [area, setArea] = useState(null);
  const areaRef = useRef(null);       // the outline being drawn (mapAreaDraw controller)
  const areaShapesRef = useRef([]);   // the finished outline + the letterboxes it would add, until they are added
  const savedAreasRef = useRef([]);   // the drop's saved outlines, dashed
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const drawnRef = useRef(new Map()); // property id -> { shape, label }
  const selectRef = useRef(() => {});
  selectRef.current = (pid) => { setSelectedId(pid); };

  useEffect(() => {
    let stale = false;
    (async () => {
      const [r, st, sr] = await Promise.all([dropsService.getById(id), settingsService.get(), surfaceRatesService.getAll()]);
      if (stale) return;
      if (st.success) setSettings(st.settings || {});
      if (sr.success) setSurfaces(sr.rates || []);
      if (r.success) setDrop(r.drop); else setError(r.error || 'Could not load the drop');
      setLoading(false);
    })();
    return () => { stale = true; };
  }, [id]);

  const properties = drop?.properties || [];
  const selected = properties.find((p) => p.id === selectedId) || null;
  useEffect(() => { setNotesDraft(selected?.notes || ''); }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const s = { total: properties.length, measured: 0, dropped: 0, no_junk_mail: 0, skipped: 0, booked: 0, estimate: 0 };
    for (const p of properties) {
      if (p.measured_at) s.measured++;
      if (s[p.status] !== undefined) s[p.status]++;
      s.estimate += Number(p.estimate_total) || 0;
    }
    return s;
  }, [properties]);

  const replaceProperty = useCallback((next) => {
    setDrop((d) => (d ? { ...d, properties: d.properties.map((p) => (p.id === next.id ? { ...p, ...next } : p)) } : d));
  }, []);

  // ---------- the map: one polygon (or dot) per letterbox ----------
  function addShape(maps, map, p, bounds) {
    const lot = parseLot(p);
    const ring = lot?.type === 'Polygon' ? lot.coordinates?.[0] : null;
    let shape;
    if (Array.isArray(ring) && ring.length >= 4) {
      const path = ring.map(([lng, lat]) => new maps.LatLng(Number(lat), Number(lng)));
      path.forEach((ll) => bounds.extend(ll));
      shape = new maps.Polygon({ map, paths: [path], strokeWeight: 1.2, zIndex: 10 });
    } else {
      bounds.extend({ lat: p.lat, lng: p.lng });
      shape = new maps.Marker({ map, position: { lat: p.lat, lng: p.lng }, icon: { path: maps.SymbolPath.CIRCLE, scale: 8, strokeWeight: 1.5 }, zIndex: 10 });
    }
    shape.addListener('click', () => selectRef.current(p.id));
    const label = new maps.Marker({
      map, position: { lat: p.lat, lng: p.lng }, clickable: true, zIndex: 20,
      icon: { path: maps.SymbolPath.CIRCLE, scale: 0 },
      label: { text: lotNumber(p), color: '#ffffff', fontSize: '11px', fontWeight: '700', className: 'dd-lot-label' }
    });
    label.addListener('click', () => selectRef.current(p.id));
    drawnRef.current.set(p.id, { shape, label, isPolygon: !!shape.getPaths });
  }
  /** Every letterbox in `list` has a shape on the map and nothing else does; returns the bounds of what was added. */
  function syncShapes(list) {
    const maps = mapsRef.current, map = mapRef.current;
    if (!maps || !map) return null;
    const keep = new Set(list.map((p) => p.id));
    for (const [key, o] of drawnRef.current) {
      if (key === 'centre' || keep.has(key)) continue;
      o.shape?.setMap(null); o.label?.setMap(null);
      drawnRef.current.delete(key);
    }
    const added = new maps.LatLngBounds();
    for (const p of list) if (!drawnRef.current.has(p.id)) addShape(maps, map, p, added);
    window.__gidDropMap = { map, count: list.length }; // headless probes
    return added;
  }
  useEffect(() => {
    if (!drop || view !== 'map' || !mapEl.current) return undefined;
    let stale = false;
    (async () => {
      try {
        const maps = await loadGoogleMaps();
        if (stale || !mapEl.current) return;
        mapsRef.current = maps;
        const map = new maps.Map(mapEl.current, {
          center: { lat: drop.centre_lat, lng: drop.centre_lng }, zoom: 18, mapTypeId: 'satellite', tilt: 0,
          disableDefaultUI: true, zoomControl: true, clickableIcons: false, gestureHandling: 'greedy'
        });
        mapRef.current = map;
        drawnRef.current = new Map();
        drawnRef.current.set('centre', { shape: new maps.Marker({ map, position: { lat: drop.centre_lat, lng: drop.centre_lng }, title: drop.centre_address || 'Centre', zIndex: 30 }) });
        const bounds = syncShapes(drop.properties);
        if (bounds && !bounds.isEmpty()) map.fitBounds(bounds, 30);
        paint(drop.properties, selectedId);
        setMapReady(true);
      } catch (e) {
        if (!stale) setError(e.message || 'Could not load the map');
      }
    })();
    return () => {
      stale = true;
      if (areaRef.current) areaRef.current.cancel();
      areaRef.current = null;
      clearAreaShapes();
      savedAreasRef.current.forEach((s) => s.setMap(null));
      savedAreasRef.current = [];
      for (const o of drawnRef.current.values()) { o.shape?.setMap(null); o.label?.setMap(null); }
      drawnRef.current = new Map();
      mapRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drop?.id, view]);
  // Letterboxes that arrive later (an area added on the map) get their shapes without rebuilding the map.
  const propertyKey = properties.map((p) => p.id).join(',');
  useEffect(() => {
    if (!mapReady) return;
    const added = syncShapes(properties);
    paint(properties, selectedId);
    if (added && !added.isEmpty()) {
      const all = new mapsRef.current.LatLngBounds();
      for (const p of properties) all.extend({ lat: p.lat, lng: p.lng });
      mapRef.current.fitBounds(all, 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, propertyKey]);
  // The outlines the drop was drawn over, dashed, so it is clear what is covered.
  useEffect(() => {
    if (!mapReady) return;
    const maps = mapsRef.current, map = mapRef.current;
    savedAreasRef.current.forEach((s) => s.setMap(null));
    savedAreasRef.current = [];
    let areas;
    try { areas = drop?.area_geojson ? JSON.parse(drop.area_geojson) : null; } catch { areas = null; }
    for (const poly of areas?.type === 'MultiPolygon' ? areas.coordinates : []) {
      const ring = poly?.[0];
      if (!Array.isArray(ring) || ring.length < 4) continue;
      savedAreasRef.current.push(new maps.Polyline({
        map, path: ring.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) })), clickable: false, zIndex: 5,
        strokeOpacity: 0, icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.85, strokeColor: '#FACC15', scale: 2.5 }, offset: '0', repeat: '12px' }]
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, drop?.area_geojson]);

  function paint(list, selId) {
    for (const p of list) {
      const o = drawnRef.current.get(p.id);
      if (!o) continue;
      const st = DROP_STATUS[p.status] || DROP_STATUS.to_do;
      const isSel = p.id === selId;
      const measured = !!p.measured_at;
      o.label.setLabel({ text: `${lotNumber(p)}${measured ? ' ✓' : ''}`, color: '#ffffff', fontSize: '11px', fontWeight: '700', className: 'dd-lot-label' });
      const baseFill = measured ? 0.34 : st.fill;
      if (o.isPolygon) {
        o.shape.setOptions({
          fillColor: measured && p.status === 'to_do' ? MEASURED_STROKE : st.colour, fillOpacity: isSel ? Math.min(0.62, baseFill + 0.2) : baseFill,
          strokeColor: isSel ? '#ffffff' : measured ? MEASURED_STROKE : '#ffffff',
          strokeOpacity: isSel || measured ? 1 : 0.6,
          strokeWeight: isSel ? 4 : measured ? 2.5 : 1.2,
          zIndex: isSel ? 15 : 10
        });
      } else {
        o.shape.setOptions({ icon: { path: mapsRef.current.SymbolPath.CIRCLE, scale: isSel ? 10 : 8, fillColor: measured && p.status === 'to_do' ? MEASURED_STROKE : st.colour, fillOpacity: 0.9, strokeColor: measured ? MEASURED_STROKE : '#ffffff', strokeWeight: isSel ? 3 : 1.5 } });
      }
    }
  }
  useEffect(() => {
    if (!mapRef.current) return;
    paint(properties, selectedId);
    const sel = properties.find((p) => p.id === selectedId);
    const b = sel && mapRef.current.getBounds();
    if (sel && b && !b.contains({ lat: sel.lat, lng: sel.lng })) mapRef.current.panTo({ lat: sel.lat, lng: sel.lng });
  }, [properties, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- status / notes ----------
  async function setStatus(p, status) {
    const r = await dropsService.updateProperty(drop.id, p.id, { status });
    if (r.success) replaceProperty(r.property); else setError(r.error || 'Could not update the letterbox');
  }
  async function saveNotes(p) {
    if ((p.notes || '') === notesDraft) return;
    const r = await dropsService.updateProperty(drop.id, p.id, { notes: notesDraft });
    if (r.success) replaceProperty(r.property); else setError(r.error || 'Could not save the note');
  }

  // ---------- tracing ----------
  const traceProperty = traceIndex === null ? null : properties[traceIndex] || null;
  // Worth tracing: not yet traced, and not a letterbox the walk will pass by (skipped / no junk mail).
  const needsTrace = (p) => !p.measured_at && p.status !== 'skipped' && p.status !== 'no_junk_mail';
  const toTrace = properties.filter(needsTrace).length;
  /**
   * The next letterbox worth tracing: the closest untraced one to the lot at `from`, so
   * working up the street or down it both carry on next door (the walking order runs
   * one way; Andrew was working the other way and kept being sent back). With no
   * anchor, the first in walking order.
   */
  function nextToTrace(from) {
    const here = properties[from];
    let best = -1, bestD = Infinity;
    properties.forEach((p, i) => {
      if (i === from || !needsTrace(p)) return;
      const d = here ? metresBetween(here, p) : i;
      if (d < bestD) { bestD = d; best = i; }
    });
    return best;
  }
  // "Trace next" carries on from the lot you are on (selected, or the one you traced last),
  // not from the start of the walk — Andrew started at 50 and was sent to No. 3 (Bug #400).
  const selectedIndex = properties.findIndex((p) => p.id === selectedId);
  const lastTracedIndex = properties.reduce((best, p, i) => (p.measured_at && (best < 0 || p.measured_at > properties[best].measured_at) ? i : best), -1);
  const anchor = selectedIndex >= 0 ? selectedIndex : lastTracedIndex;
  const firstUntraced = anchor >= 0 && needsTrace(properties[anchor]) ? anchor : nextToTrace(anchor);
  function openTrace(pid) {
    const i = properties.findIndex((p) => p.id === pid);
    if (i >= 0) { setSelectedId(pid); setTraceIndex(i); }
  }
  function stepTrace(delta) {
    setTraceIndex((i) => {
      const n = i + delta;
      if (n < 0 || n >= properties.length) return i;
      setSelectedId(properties[n].id);
      return n;
    });
  }
  async function applyTrace(result) {
    const p = traceProperty;
    if (!p) return;
    // Drawn on another house? (Bug #400: 54/56/58 went under 3/7/9/13 and 60 under 50 after the map was
    // dragged along the street.) The lot the outline sits in wins; on the verge, the nearest house does
    // once the header's house is clearly not it.
    let target = p;
    const c = firstCentroid(result.measurements);
    const holder = c && lotHolding(c, properties);
    const nearest = c && nearestLetterbox(c, properties);
    const own = c ? metresBetween(p, { lat: c[1], lng: c[0] }) : 0;
    const other = holder ? (holder.id !== p.id ? holder : null) : (nearest && nearest.id !== p.id && own > 30 ? nearest : null);
    if (other) {
      if (other.measured_at && !window.confirm(`That outline is on ${shortAddress(other)}'s lot, which is already traced. Replace ${shortAddress(other)}'s trace with it?`)) return;
      if (!other.measured_at && !holder && !window.confirm(`That outline is nearer ${shortAddress(other)} than ${shortAddress(p)}. Save it under ${shortAddress(other)}?`)) return;
      target = other; // inside another lot: no question, that is the house it belongs to
    } else if (!holder && own > 45 && !window.confirm(`The outline you drew is about ${Math.round(own)} m from ${shortAddress(p)} — it looks like it is on another house. Save it under ${shortAddress(p)} anyway?`)) return;
    setSaving(true);
    const r = await dropsService.saveMeasurements(drop.id, target.id, result.measurements);
    setSaving(false);
    if (!r.success) { setError(r.error || 'Could not save the traced surfaces'); return; }
    replaceProperty(r.property);
    setLastSaved(shortAddress(target));
    const next = nextToTrace(properties.findIndex((x) => x.id === target.id));
    if (next >= 0 && next !== traceIndex) { setSelectedId(properties[next].id); setTraceIndex(next); } else setTraceIndex(null);
  }
  const context = useMemo(() => ({
    lots: properties.map((p) => ({ id: p.id, lat: p.lat, lng: p.lng, label: lotNumber(p), lot: parseLot(p), traced: !!p.measured_at, area: tracedArea(p), shapes: surfacesOf(p).map((m) => ({ kind: m.kind, geojson: m.geojson })) }))
  }), [properties]);
  async function skipTrace() {
    const p = traceProperty;
    if (!p) return;
    const r = await dropsService.updateProperty(drop.id, p.id, { status: 'skipped' });
    if (!r.success) { setError(r.error || 'Could not skip the letterbox'); return; }
    replaceProperty(r.property);
    setLastSaved(`${shortAddress(p)} skipped`);
    const next = nextToTrace(traceIndex);
    if (next >= 0) { setSelectedId(properties[next].id); setTraceIndex(next); } else setTraceIndex(null);
  }
  // ---------- add an area (backlog #694): outline the streets the radius missed ----------
  function clearAreaShapes() {
    areaShapesRef.current.forEach((s) => s.setMap(null));
    areaShapesRef.current = [];
  }
  function startArea() {
    const maps = mapsRef.current, map = mapRef.current;
    if (!maps || !map || areaRef.current) return;
    clearAreaShapes();
    setNotice(''); setError('');
    setArea({ phase: 'drawing', corners: 0 });
    areaRef.current = startAreaDraw(maps, map, {
      ignore: [...drawnRef.current.values()].flatMap((o) => [o.shape, o.label]),
      onChange: (n) => setArea({ phase: 'drawing', corners: n }),
      onCancel: () => { areaRef.current = null; setArea(null); },
      onFinish: (ring) => { areaRef.current = null; checkArea(ring); }
    });
  }
  async function checkArea(ring) {
    setArea({ phase: 'checking', ring });
    const r = await dropsService.extendPreview(drop.id, ring);
    if (!r.success) { setArea(null); setError(r.error || 'Could not look up the letterboxes in that area'); return; }
    const maps = mapsRef.current, map = mapRef.current;
    if (maps && map) {
      clearAreaShapes();
      areaShapesRef.current.push(new maps.Polygon({ map, paths: [ring.map(([lat, lng]) => ({ lat, lng }))], strokeColor: '#FACC15', strokeOpacity: 0.95, strokeWeight: 2.5, fillColor: '#FACC15', fillOpacity: 0.06, clickable: false, zIndex: 5 }));
      for (const p of r.properties || []) {
        areaShapesRef.current.push(new maps.Marker({
          map, position: { lat: p.lat, lng: p.lng }, clickable: false, zIndex: 25, title: p.address,
          icon: { path: maps.SymbolPath.CIRCLE, scale: 6, fillColor: MEASURED_STROKE, fillOpacity: 0.95, strokeColor: '#0b0f14', strokeWeight: 1.5 }
        }));
      }
    }
    setArea({ phase: 'preview', ring, count_new: r.count_new, count_existing: r.count_existing, properties: r.properties || [] });
  }
  async function addArea() {
    if (!area?.ring) return;
    setArea({ ...area, phase: 'adding' });
    const r = await dropsService.extend(drop.id, area.ring);
    if (!r.success) { setArea({ ...area, phase: 'preview' }); setError(r.error || 'Could not add the area to the drop'); return; }
    clearAreaShapes();
    setArea(null);
    setDrop(r.drop);
    setNotice(`${r.added} letterbox${r.added === 1 ? '' : 'es'} added — the walk is now ${r.drop.property_count} letterboxes, re-ordered street by street. Nothing already traced or ticked has changed.`);
  }
  function cancelArea() {
    if (areaRef.current) areaRef.current.cancel();
    areaRef.current = null;
    clearAreaShapes();
    setArea(null);
  }
  function redrawArea() {
    cancelArea();
    startArea();
  }

  const site = traceProperty ? {
    id: traceProperty.id, lat: traceProperty.lat, lng: traceProperty.lng,
    title: shortAddress(traceProperty),
    subtitle: [traceProperty.locality, traceProperty.lotplan, traceProperty.lot_area_m2 ? `lot ${Math.round(traceProperty.lot_area_m2)} m²` : null].filter(Boolean).join(' · '),
    lot: parseLot(traceProperty)
  } : null;

  if (loading) return <div className="dd-page"><div className="empty-state"><div className="loading-spinner"></div></div></div>;
  if (!drop) return <div className="dd-page"><p className="form-error">{error || 'Drop not found'}</p><Link to="/drops">← All drops</Link></div>;

  return (
    <div className="dd-page">
      <div className="page-header dd-header">
        <div className="dd-title">
          <Link to="/drops" className="text-muted dd-back">← Drops</Link>
          <h1 className="page-title">{drop.name}</h1>
          <p className="text-muted">
            {drop.centre_address}{drop.job_number ? ` · ${drop.job_number}${drop.job_title ? ` ${drop.job_title}` : ''}` : ''} · {stats.total} letterboxes{drop.area_geojson ? ' · with a drawn area' : ` within ${drop.radius_m} m`}
          </p>
        </div>
        <div className="dd-actions">
          <div className="dd-toggle" role="tablist" aria-label="View">
            <button type="button" role="tab" aria-selected={view === 'map'} className={view === 'map' ? 'active' : ''} onClick={() => setView('map')}>Map</button>
            <button type="button" role="tab" aria-selected={view === 'walk'} className={view === 'walk' ? 'active' : ''} onClick={() => setView('walk')}>Walk list</button>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => openTrace(properties[firstUntraced]?.id)} disabled={firstUntraced < 0}
            title={firstUntraced < 0 ? 'Every letterbox is traced' : 'Trace the driveways one after another, in walking order'}>
            {stats.measured === 0 ? 'Start tracing' : `Trace next (${toTrace} left)`}
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={startArea} disabled={view !== 'map' || !mapReady || !!area}
            title={view !== 'map' ? 'Switch to the map to draw an area' : 'Outline more streets on the map and add every letterbox inside'}>Add an area</button>
          <a className="btn btn-outline btn-sm" href={dropsService.csvUrl(drop.id)} download>Drop sheet (CSV)</a>
        </div>
      </div>

      <div className="dd-stats">
        <span><strong>{stats.measured}</strong> traced</span>
        <span><strong>{stats.dropped}</strong> dropped</span>
        <span><strong>{stats.no_junk_mail}</strong> no junk mail</span>
        {stats.skipped > 0 && <span><strong>{stats.skipped}</strong> skipped</span>}
        {stats.booked > 0 && <span><strong>{stats.booked}</strong> booked</span>}
        <span><strong>{formatMoney(stats.estimate)}</strong> of driveways traced</span>
      </div>
      {error && <p className="form-error">{error} <button type="button" className="est-mini" onClick={() => setError('')}>dismiss</button></p>}
      {notice && <p className="dd-notice">{notice} <button type="button" className="est-mini" onClick={() => setNotice('')}>dismiss</button></p>}

      {view === 'map' ? (
        <div className="dd-body">
          <div className="dd-map-wrap">
            <div className="dd-map" ref={mapEl} />
            <div className="dd-legend">
              {Object.entries(DROP_STATUS).map(([k, s]) => <span key={k}><i style={{ background: s.colour }} /> {s.label}</span>)}
              <span><i className="measured" /> traced</span>
            </div>
            {area?.phase === 'drawing' && (
              <AreaDrawBar count={area.corners} doneLabel="Find the letterboxes" onUndo={() => areaRef.current?.undo()} onCancel={cancelArea} onDone={() => areaRef.current?.finish()} />
            )}
            {area?.phase === 'checking' && <div className="area-draw-bar result"><p><strong>Looking up the letterboxes inside the outline…</strong></p></div>}
            {(area?.phase === 'preview' || area?.phase === 'adding') && (
              <div className="area-draw-bar result">
                <p>
                  <strong>{area.count_new} new letterbox{area.count_new === 1 ? '' : 'es'}</strong> inside the outline
                  {area.count_existing > 0 ? ` · ${area.count_existing} already in the drop` : ''}
                  {area.count_new === 0 ? ' — nothing to add.' : '. They join the walk in street order; the houses already here keep their status and traces.'}
                </p>
                <div className="area-draw-actions">
                  <button type="button" className="btn btn-sm btn-outline" onClick={cancelArea} disabled={area.phase === 'adding'}>Cancel</button>
                  <button type="button" className="btn btn-sm btn-outline" onClick={redrawArea} disabled={area.phase === 'adding'}>Draw again</button>
                  <button type="button" className="btn btn-sm btn-primary" onClick={addArea} disabled={area.phase === 'adding' || area.count_new === 0}>{area.phase === 'adding' ? 'Adding…' : `Add ${area.count_new}`}</button>
                </div>
              </div>
            )}
          </div>
          <aside className="dd-panel">
            {selected ? (
              <PropertyCard p={selected} onStatus={(s) => setStatus(selected, s)} onTrace={() => openTrace(selected.id)}
                notes={notesDraft} setNotes={setNotesDraft} onSaveNotes={() => saveNotes(selected)} />
            ) : (
              <div className="dd-panel-empty">
                <p>Tap a lot on the map to see it, mark what happened at the letterbox, or trace its driveway.</p>
                <p className="text-muted">In the tracer the dashed yellow line shows which house you are on. Trace whichever driveway you like — it is saved under the lot it sits on — and "next" goes to the nearest untraced house in the direction you are working.</p>
                <p className="text-muted">Missing a street? <strong>Add an area</strong> lets you outline it on the map — every letterbox inside joins the walk.</p>
              </div>
            )}
          </aside>
        </div>
      ) : (
        <WalkList properties={properties} selectedId={selectedId} onSelect={setSelectedId} onStatus={setStatus} onTrace={openTrace} />
      )}

      <EstimatorModal
        isOpen={!!traceProperty}
        onClose={() => { setTraceIndex(null); setLastSaved(''); }}
        variant="drop"
        site={site}
        context={context}
        surfaces={surfaces}
        settings={settings}
        initial={traceProperty?.measurements || []}
        draftId={{ propertyId: traceProperty?.id || null }}
        savedAt={traceProperty?.measured_at || null}
        applyLabel={saving ? 'Saving…' : 'Save driveway'}
        step={traceProperty ? {
          label: `${traceIndex + 1} of ${properties.length} · ${stats.measured} traced${lastSaved ? ` · ${lastSaved} ✓` : ''}`,
          canPrev: traceIndex > 0, canNext: traceIndex < properties.length - 1,
          onPrev: () => stepTrace(-1), onNext: () => stepTrace(1), onSkip: skipTrace
        } : null}
        onApply={applyTrace}
        priceLinesDefault
        groupDefault={false}
      />
    </div>
  );
}

function PropertyCard({ p, onStatus, onTrace, notes, setNotes, onSaveNotes }) {
  const st = DROP_STATUS[p.status] || DROP_STATUS.to_do;
  const list = surfacesOf(p);
  return (
    <div className="dd-card">
      <div className="dd-card-head">
        <div>
          <h2>{shortAddress(p)}</h2>
          <p className="text-muted">{p.locality} · #{p.sort_order} on the walk · {p.distance_m} m from the job{p.lot_area_m2 ? ` · lot ${Math.round(p.lot_area_m2)} m²` : ''}</p>
        </div>
        <span className="dd-status-pill" style={{ borderColor: st.colour, color: st.colour }}>{st.label}</span>
      </div>

      <div className="dd-section">
        <div className="dd-section-head"><strong>Driveway</strong>{p.measured_at && <span className="text-muted">traced {fmtTime(p.measured_at)}</span>}</div>
        {list.length ? (
          <ul className="dd-surfaces">
            {list.map((m) => <li key={m.id}><span>{m.label}</span><span>{fmtMeasure(m.measured_value, m.measure === 'length' ? 'lm' : 'm2')}</span></li>)}
            <li className="total"><span>Estimate</span><span>{p.estimate_total === null ? '—' : formatMoney(p.estimate_total)}</span></li>
          </ul>
        ) : <p className="text-muted">Not traced yet.</p>}
        <button type="button" className="btn btn-sm btn-primary" onClick={onTrace}>{list.length ? 'Re-trace' : 'Trace the driveway'}</button>
      </div>

      <div className="dd-section">
        <div className="dd-section-head"><strong>At the letterbox</strong>{p.status_at && <span className="text-muted">{fmtTime(p.status_at)}</span>}</div>
        <StatusButtons status={p.status} onStatus={onStatus} />
      </div>

      <div className="dd-section">
        <label htmlFor="dd_notes"><strong>Note</strong></label>
        <textarea id="dd_notes" rows="2" placeholder="Dog, gate, spoke to the owner…" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={onSaveNotes} />
      </div>
    </div>
  );
}

function StatusButtons({ status, onStatus, compact = false }) {
  const order = compact ? ['dropped', 'no_junk_mail', 'skipped'] : ['dropped', 'no_junk_mail', 'skipped', 'booked'];
  return (
    <div className={`dd-status-buttons ${compact ? 'compact' : ''}`}>
      {order.map((k) => (
        <button key={k} type="button" className={`dd-status-btn ${status === k ? 'active' : ''}`} style={{ '--c': DROP_STATUS[k].colour }}
          onClick={() => onStatus(status === k ? 'to_do' : k)} title={status === k ? 'Undo' : DROP_STATUS[k].label}>
          {DROP_STATUS[k].label}
        </button>
      ))}
    </div>
  );
}

/** The day itself: every letterbox in walking order, big buttons, nothing else. */
function WalkList({ properties, selectedId, onSelect, onStatus, onTrace }) {
  let lastStreet = null;
  return (
    <div className="dd-walk">
      {properties.map((p) => {
        const street = `${p.street_full || ''}, ${p.locality || ''}`;
        const heading = street !== lastStreet ? street : null;
        lastStreet = street;
        const st = DROP_STATUS[p.status] || DROP_STATUS.to_do;
        return (
          <div key={p.id}>
            {heading && <h3 className="dd-walk-street">{heading}</h3>}
            <div className={`dd-walk-row ${p.status !== 'to_do' ? 'done' : ''} ${p.id === selectedId ? 'selected' : ''}`} onClick={() => onSelect(p.id)}>
              <div className="dd-walk-main">
                <span className="dd-walk-number" style={{ borderColor: st.colour }}>{p.unit_number ? `U${p.unit_number}/` : ''}{p.street_number || '·'}</span>
                <div className="dd-walk-text">
                  <strong>{shortAddress(p)}</strong>
                  <span className="text-muted">
                    {p.measured_at ? `${formatMoney(p.estimate_total || 0)} · ${surfacesOf(p).map((m) => fmtMeasure(m.measured_value, m.measure === 'length' ? 'lm' : 'm2')).join(' + ')}` : 'not traced'}
                    {p.notes ? ` · ${p.notes}` : ''}
                  </span>
                </div>
              </div>
              <div className="dd-walk-actions" onClick={(e) => e.stopPropagation()}>
                {p.status === 'to_do'
                  ? <StatusButtons status={p.status} onStatus={(s) => onStatus(p, s)} compact />
                  : <><span className="dd-status-pill" style={{ borderColor: st.colour, color: st.colour }}>{st.label}</span><button type="button" className="est-mini" onClick={() => onStatus(p, 'to_do')}>undo</button></>}
                {!p.measured_at && <button type="button" className="est-mini" onClick={() => onTrace(p.id)}>trace</button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
